<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('ProjectSettings|Task Hierarchy') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <p>
              {{
                s__(
                  'ProjectSettings|Task hierarchy allows you to organize tasks in a parent-child structure, creating a tree-like relationship between tasks. This helps break down complex work into manageable subtasks.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|How It Works') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|When enabled, you can create parent tasks and assign child tasks to them. Tasks can have up to 3 levels of hierarchy: parent tasks, child tasks, and grandchild tasks. This structure helps organize work and track progress at different levels of detail.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Example: 3-Level Hierarchy') }}</h4>
            <div
              style="
                margin-bottom: 16px;
                padding: 12px;
                background: #f5f5f5;
                border-radius: 8px;
                font-family: monospace;
                font-size: 14px;
                line-height: 1.8;
              "
            >
              <div style="font-weight: 600; color: #333">
                📋 {{ s__('ProjectSettings|Level 1: Project Launch') }}
              </div>
              <div style="margin-left: 20px; color: #666">
                📋 {{ s__('ProjectSettings|Level 2: Design Phase') }}
                <div style="margin-left: 20px; color: #888">
                  📋 {{ s__('ProjectSettings|Level 3: Create wireframes') }}<br />
                  📋 {{ s__('ProjectSettings|Level 3: Design mockups') }}<br />
                  📋 {{ s__('ProjectSettings|Level 3: Review designs') }}
                </div>
              </div>
              <div style="margin-left: 20px; color: #666">
                📋 {{ s__('ProjectSettings|Level 2: Development Phase') }}
                <div style="margin-left: 20px; color: #888">
                  📋 {{ s__('ProjectSettings|Level 3: Backend implementation') }}<br />
                  📋 {{ s__('ProjectSettings|Level 3: Frontend implementation') }}<br />
                  📋 {{ s__('ProjectSettings|Level 3: Integration testing') }}
                </div>
              </div>
              <div style="margin-left: 20px; color: #666">
                📋 {{ s__('ProjectSettings|Level 2: Testing Phase') }}
                <div style="margin-left: 20px; color: #888">
                  📋 {{ s__('ProjectSettings|Level 3: Unit tests') }}<br />
                  📋 {{ s__('ProjectSettings|Level 3: Integration tests') }}<br />
                  📋 {{ s__('ProjectSettings|Level 3: User acceptance testing') }}
                </div>
              </div>
            </div>

            <h4>{{ s__('ProjectSettings|Benefits') }}</h4>
            <ul style="margin: 0 0 16px 0; padding-left: 20px; color: #666">
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Break down large tasks into smaller, manageable pieces') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Track progress at different levels of granularity') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Organize related tasks together in a logical structure') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Improve project planning and resource allocation') }}
              </li>
            </ul>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('ProjectSettings|Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from '../common/icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 20px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}

.info-section ul {
  line-height: 1.6;
}
</style>
